/**
 * EN-5191: a test aid that shows, in the running portal, which strings were served by the
 * API and which fell back to the bundle built into the app (`src/locales/en-GB.json`).
 *
 * Off by default. Turn it on with `?i18nDebug=1` in the URL (it sticks for the tab), and off
 * again with `?i18nDebug=0`. It is deliberately NOT gated on the build type: CRA sets
 * NODE_ENV=production for every `npm run build:*`, including development and staging, so a
 * NODE_ENV gate would disable it exactly where the testing happens.
 *
 * It is NOT harmless, and an earlier version of this comment said it was. The markers go into
 * the i18next store, so anything that *reads copy back out of the store in order to save it*
 * would persist them. That was a real defect: CreateTriggerModal built the trigger's stored
 * hcpMessage from `getFixedT("en-GB")` and so saved "⟦An alert will be sent…⟧" for everyone
 * else to read. Stored copy now comes from an isolated instance (config/persistedCopy) that
 * nothing here can reach, which is the invariant that makes this aid safe — not the fact that
 * it only decorates text. Unlike ?pseudo=1 this has no account gate, so it is reachable by any
 * signed-in user with the link.
 *
 * Two signals, because one is not enough on its own:
 *
 *  - Every API-served value is wrapped in ⟦…⟧. This works everywhere, including strings
 *    handed to another component as a prop (`placeholder`, `label`, `title`), which is most
 *    of them.
 *  - Where the string is rendered as text, its element is also coloured red. This cannot
 *    work for the prop cases: by the time the string reaches `<input placeholder>` there is
 *    no element of its own to style.
 *
 * So: red where it can be, brackets everywhere.
 */

import { isPMPUser } from "./isPMPUser";

export const I18N_DEBUG_STORAGE_KEY = "i18nDebug";

/** U+27E6 / U+27E7. Chosen because nothing in the portal's copy uses them. */
export const API_MARK_START = "⟦";
export const API_MARK_END = "⟧";

const HIGHLIGHT_COLOUR = "#d32f2f";
/**
 * What the DOM normalises HIGHLIGHT_COLOUR to when it is read back. Comparing against the hex
 * would never match, so the "already coloured" guard below would re-assign on every mutation.
 */
const HIGHLIGHT_COLOUR_COMPUTED = "rgb(211, 47, 47)";

/**
 * Decided once per page load and reused, for the same reason as the pseudo-locale aid: signing
 * in changes isPMPUser() mid-session, and a flag that flipped between the baseline bundle and
 * the published one would mark only half the strings.
 */
let cachedDecision: boolean | undefined;

/** Test seam. Nothing in the app should call this — the value is meant to be fixed. */
export function resetI18nSourceDebugCache(): void {
  cachedDecision = undefined;
}

function readFlag(): boolean {
  try {
    const param = new URLSearchParams(window.location.search).get("i18nDebug");

    if (param === "1") {
      window.sessionStorage.setItem(I18N_DEBUG_STORAGE_KEY, "1");
      return true;
    }

    if (param === "0") {
      window.sessionStorage.removeItem(I18N_DEBUG_STORAGE_KEY);
      return false;
    }

    return window.sessionStorage.getItem(I18N_DEBUG_STORAGE_KEY) === "1";
  } catch {
    // Private mode, or storage blocked. The debug aid is never worth breaking a render for.
    return false;
  }
}

function clearFlag(): void {
  try {
    window.sessionStorage.removeItem(I18N_DEBUG_STORAGE_KEY);
  } catch {
    // Nothing to do: the flag is already unreachable, which is the state we wanted.
  }
}

/**
 * Reads the flag, letting `?i18nDebug=1|0` set or clear the stored preference first so the
 * URL always wins over what a previous visit left behind.
 *
 * **Restricted to patientMpower accounts**, like `?pseudo=1`. This one does less damage — it
 * brackets and reddens text rather than making the portal unreadable — but a link carrying it
 * still has no business doing anything in a customer's hands, and it exposes which strings come
 * from the API and which from the build. A request from anyone else is refused *and* the stored
 * flag cleared, so it cannot sit in the tab waiting for the next person to sign in. Signed out
 * counts as anyone else: there is no account to check.
 */
export function isI18nSourceDebugEnabled(): boolean {
  if (cachedDecision !== undefined) return cachedDecision;

  const requested = readFlag();

  if (requested && !isPMPUser()) {
    clearFlag();
    cachedDecision = false;
    return false;
  }

  cachedDecision = requested;
  return requested;
}

/**
 * Returns a copy of an i18next resource tree with every leaf wrapped in the markers.
 *
 * The input is not mutated: the same parsed object is what React Query has cached, and
 * marking it in place would double-wrap on the next render.
 *
 * Interpolation placeholders (`{{max}}`) and `<Trans>` tags (`<bold>…</bold>`) sit inside
 * the wrapper untouched, so a marked string still interpolates and still renders its markup.
 */
export function markApiTranslations(resources: unknown): unknown {
  if (typeof resources === "string") {
    return `${API_MARK_START}${resources}${API_MARK_END}`;
  }

  if (resources === null || typeof resources !== "object") {
    return resources;
  }

  return Object.fromEntries(
    Object.entries(resources as Record<string, unknown>).map(([key, value]) => [
      key,
      markApiTranslations(value),
    ])
  );
}

function highlightTextNode(node: Node): void {
  if (!node.nodeValue?.includes(API_MARK_START)) return;

  const element = node.parentElement;
  // Only the colour is set, never the DOM structure. Wrapping the text node in a <span>
  // would insert a node inside a tree React owns, and React's next reconciliation of
  // that parent throws when the child it expects is no longer there.
  if (element && element.style.color !== HIGHLIGHT_COLOUR_COMPUTED) {
    element.style.color = HIGHLIGHT_COLOUR;
  }
}

function highlightWithin(root: Node): void {
  // A TreeWalker never yields its own root, and React inserts and updates bare text nodes —
  // so the root has to be checked separately or every such string is missed.
  if (root.nodeType === Node.TEXT_NODE) {
    highlightTextNode(root);
    return;
  }

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    highlightTextNode(node);
  }
}

/**
 * Colours every element whose text carries the marker, and keeps doing so as the app
 * re-renders. Returns a teardown function.
 */
export function startI18nSourceHighlight(): () => void {
  highlightWithin(document.body);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "characterData") {
        highlightWithin(mutation.target.parentNode ?? mutation.target);
        return;
      }

      mutation.addedNodes.forEach((added) => highlightWithin(added));
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  return () => observer.disconnect();
}
